/**
 * pages/Placeholder.tsx — honest-placeholder pages for endpoints that
 * aren't wired yet.
 *
 * The user's rule is explicit: legally-required rows (close-account,
 * report-a-problem-with-a-transaction) MUST stay visible even before
 * their endpoints exist. This component satisfies the rule with:
 *
 *   - A plain statement that the feature is not live yet.
 *   - A working support contact.
 *   - NO form that submits nowhere.
 *
 * Route: /:section/:slug (e.g. /settings/close-account)
 *
 * The content dictionary lives inside the file so a reviewer can
 * grep for the exact copy shipped for each row.
 */

import { useParams } from "react-router-dom";
import { Button, Card } from "../components";
import { AppShell } from "./AppShell";
import type { NavKey } from "./AppShell";
import styles from "./Placeholder.module.css";

// ---------------------------------------------------------------------------
// Content dictionary. One entry per honest-placeholder route.
// ---------------------------------------------------------------------------

interface PlaceholderContent {
  eyebrow: string;
  title: string;
  intro: string;
  bullets: string[];
  supportLine: string;
  legalRef?: string;
  current: NavKey;
}

const CONTENT: Record<string, PlaceholderContent> = {
  "close-account": {
    eyebrow: "Account",
    title: "Close your account",
    intro:
      "You have the right to delete your AsterCash account and every record we hold that isn't required by regulation to retain.",
    bullets: [
      "The self-serve deletion flow is being finalised. Until it ships, you can request closure directly.",
      "Once complete, closure is permanent: balances withdrawn, linked funding sources removed, records retained only for the mandatory five-year window then destroyed.",
      "You will receive a written confirmation within one business day of your request.",
    ],
    supportLine:
      "Email close@astercash.com from the address on your account with the words “Close my account” in the subject line. Include your handle.",
    legalRef: "App Store §5.1.1 — in-app account deletion",
    current: "settings",
  },
  "report-problem": {
    eyebrow: "Help",
    title: "Report a problem with a transaction",
    intro:
      "If a transaction on your account is wrong, missing, unauthorised, or was reversed unexpectedly, you have federal rights under Regulation E to have it investigated.",
    bullets: [
      "Notify us within 60 days of the statement showing the error to preserve full Reg E protection.",
      "We will investigate and either correct the error or explain why not, in writing, within the timelines Regulation E requires (typically 10 business days; up to 45 for complex cases with provisional credit).",
      "The dispute-submission form is being finalised. Until it ships, disputes are handled by email — every one receives a case number within one business day.",
    ],
    supportLine:
      "Email disputes@astercash.com from the address on your account. Include the transaction date, amount, counterparty, and a one-line description of what went wrong.",
    legalRef: "Reg E §1005.11 — error resolution",
    current: "settings",
  },
  profile: {
    eyebrow: "Account",
    title: "Profile",
    intro:
      "Handle, display name, and avatar. Profile editing on web is coming soon.",
    bullets: [
      "For now, edit your profile in the AsterCash mobile app — changes there sync to this browser automatically.",
      "Your handle is permanent once verified; contact support to change it.",
    ],
    supportLine: "Questions? support@astercash.com",
    current: "settings",
  },
  identity: {
    eyebrow: "Account",
    title: "Identity & limits",
    intro:
      "The verification tier on your account controls per-transaction and monthly volume limits. This screen will surface them soon.",
    bullets: [
      "You can view your current tier and limits in the AsterCash mobile app under Settings → Identity.",
      "To raise your tier, complete the identity-verification flow in the mobile app.",
    ],
    supportLine: "Questions? support@astercash.com",
    current: "settings",
  },
  "funding-sources": {
    eyebrow: "Money",
    title: "Funding sources",
    intro:
      "Bank accounts and cards you use to add money to or send money from your AsterCash balance.",
    bullets: [
      "Adding or removing a funding source is a device-only action — it happens in the mobile app.",
      "This screen will show every source linked to your account, along with the last time it was used.",
    ],
    supportLine: "Questions? support@astercash.com",
    current: "settings",
  },
  export: {
    eyebrow: "Money",
    title: "Export data",
    intro:
      "Download a copy of your account records — activity, statements, and linked funding sources — in machine-readable formats.",
    bullets: [
      "CSV and JSON exports are being finalised.",
      "Statement PDFs are already available on the Statements screen once the automated download lands.",
    ],
    supportLine: "For an immediate export, email support@astercash.com.",
    current: "settings",
  },
  passkeys: {
    eyebrow: "Security",
    title: "Passkeys",
    intro:
      "Register or revoke passkeys used to sign into AsterCash from this browser.",
    bullets: [
      "Passkey management on web is coming soon.",
      "For now, enrol passkeys from the mobile app under Settings → Security → Passkeys.",
    ],
    supportLine: "Questions? support@astercash.com",
    current: "settings",
  },
  sessions: {
    eyebrow: "Security",
    title: "Active sessions",
    intro:
      "Every place you're currently signed in, with a revoke button next to each.",
    bullets: [
      "The full sessions list is being finalised.",
      "You can already sign out of THIS browser from the button at the bottom of Settings.",
    ],
    supportLine:
      "If you suspect your account is compromised, email security@astercash.com immediately.",
    current: "settings",
  },
  devices: {
    eyebrow: "Security",
    title: "Registered devices",
    intro:
      "Phones and browsers approved for this account. Only registered devices can approve money movement.",
    bullets: [
      "Device management on web is coming soon.",
      "For now, register or revoke devices from the mobile app.",
    ],
    supportLine: "Questions? support@astercash.com",
    current: "settings",
  },
  support: {
    eyebrow: "Help",
    title: "Contact support",
    intro:
      "Real people, real replies. We aim to respond to every message within one business day.",
    bullets: [
      "General questions: support@astercash.com",
      "Disputes and transaction errors: disputes@astercash.com",
      "Security concerns: security@astercash.com",
      "Account closure: close@astercash.com",
    ],
    supportLine:
      "For anything urgent, include “URGENT” in the subject and we'll triage that day.",
    current: "settings",
  },
  "data-export-request": {
    eyebrow: "Legal",
    title: "Data export request",
    intro:
      "Under applicable privacy laws (CCPA, GDPR, and equivalents), you may request a full copy of every piece of information we hold about you.",
    bullets: [
      "We fulfill data-export requests within 45 days of receipt (30 days in most jurisdictions).",
      "Requests are free the first time in any twelve-month period.",
    ],
    supportLine:
      "Email privacy@astercash.com from the address on your account with the subject “Data export request”.",
    current: "settings",
  },
  "data-deletion-request": {
    eyebrow: "Legal",
    title: "Data deletion request",
    intro:
      "You may request that we delete personal information we hold about you, subject to the legal retention obligations described in our Privacy Policy.",
    bullets: [
      "Records we're required to retain (transaction logs under Bank Secrecy Act, audit trail under Reg E, etc.) will be retained only for the minimum period.",
      "All other personal data will be permanently deleted within 30 days of confirmation.",
    ],
    supportLine:
      "Email privacy@astercash.com from the address on your account with the subject “Data deletion request”.",
    current: "settings",
  },
};

// ---------------------------------------------------------------------------
// Page.
// ---------------------------------------------------------------------------

export function Placeholder() {
  const params = useParams();
  const slug = params.slug || "";
  const content = CONTENT[slug];

  if (!content) {
    return (
      <AppShell current="settings">
        <header className={styles.pageHeader}>
          <div>
            <p className={styles.eyebrow}>Not found</p>
            <h1 className={styles.pageTitle}>Nothing at that URL</h1>
          </div>
        </header>
        <p className={styles.leadCopy}>
          Go back to <a href="/settings" className={styles.link}>Settings</a>.
        </p>
      </AppShell>
    );
  }

  return (
    <AppShell current={content.current}>
      <header className={styles.pageHeader}>
        <div>
          <p className={styles.eyebrow}>{content.eyebrow}</p>
          <h1 className={styles.pageTitle}>{content.title}</h1>
        </div>
      </header>

      <p className={styles.leadCopy}>{content.intro}</p>

      <Card className={styles.honestBanner}>
        <p className={styles.honestBannerTitle}>
          <span className={styles.honestGlyph} aria-hidden>
            ⓘ
          </span>{" "}
          Automated flow not live yet
        </p>
        <p className={styles.honestBannerBody}>
          The self-serve version of this screen is being finalised. Everything
          this page promises is still available today — via the support address
          below. Requests are handled by real people, with a case number
          returned within one business day.
        </p>
      </Card>

      <ul className={styles.bullets}>
        {content.bullets.map((b, i) => (
          <li key={i} className={styles.bullet}>
            {b}
          </li>
        ))}
      </ul>

      <Card className={styles.supportCard} padding={0}>
        <div className={styles.supportInner}>
          <p className={styles.supportEyebrow}>How to reach us</p>
          <p className={styles.supportLine}>{content.supportLine}</p>
        </div>
      </Card>

      {content.legalRef && (
        <p className={styles.legalRef}>
          This screen exists because of{" "}
          <strong>{content.legalRef}</strong>. It stays visible even before
          the self-serve flow ships.
        </p>
      )}

      <div className={styles.backCta}>
        <Button
          variant="secondary"
          onClick={() => {
            window.history.back();
          }}
        >
          Back
        </Button>
      </div>
    </AppShell>
  );
}
